import React from 'react';
import { Home, MapPin, GraduationCap, CheckSquare, TrendingUp, Map as MapIcon, Users, BookOpen } from 'lucide-react';
import { AppView } from '../types';

interface NavbarProps {
  currentView: AppView;
  setView: (view: AppView) => void;
}

type NavItem = { id: AppView; label: string; icon: React.ElementType };
type NavGroup = { label: string; color: string; items: NavItem[] };

const NAV_GROUPS: NavGroup[] = [
  {
    label: '',
    color: '',
    items: [
      { id: AppView.DASHBOARD, label: 'Home', icon: Home },
    ],
  },
  {
    label: '📍 Pune Guide',
    color: 'text-amber-600',
    items: [
      { id: AppView.MAP, label: 'Map', icon: MapIcon },
      { id: AppView.NEIGHBORHOODS, label: 'Localities', icon: MapPin },
      { id: AppView.SCHOOLS, label: 'Schools', icon: GraduationCap },
    ],
  },
  {
    label: '🇮🇳 NRI Blueprint',
    color: 'text-indigo-500',
    items: [
      { id: AppView.CHECKLIST, label: 'Checklist', icon: CheckSquare },
      { id: AppView.INVESTMENTS, label: 'Money', icon: TrendingUp },
      { id: AppView.COMMUNITY, label: 'Q&A', icon: Users },
      { id: AppView.GUIDES, label: 'Guides', icon: BookOpen },
    ],
  },
];

// Flat list for mobile bottom bar (no dividers)
const ALL_ITEMS: NavItem[] = NAV_GROUPS.flatMap(g => g.items);

const Navbar: React.FC<NavbarProps> = ({ currentView, setView }) => {
  if (currentView === AppView.QUIZ) return null;

  return (
    <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 md:static md:h-screen md:w-64 md:border-t-0 md:border-r md:flex md:flex-col shadow-lg z-50">
      {/* Desktop logo */}
      <div className="hidden md:flex items-center justify-center h-20 border-b border-slate-100">
        <h1 className="text-2xl font-bold text-indigo-600">NRI<span className="text-slate-800">Guide</span></h1>
      </div>

      {/* Desktop sidebar — grouped */}
      <div className="hidden md:flex md:flex-col md:p-4 md:space-y-1 md:overflow-y-auto flex-1">
        {NAV_GROUPS.map((group, gi) => (
          <div key={gi}>
            {group.label && (
              <div className={`flex items-center gap-2 px-2 pt-4 pb-1 ${gi > 0 ? 'mt-1' : ''}`}>
                <span className={`text-[10px] font-bold uppercase tracking-widest ${group.color}`}>
                  {group.label}
                </span>
              </div>
            )}
            {group.items.map((item) => {
              const Icon = item.icon;
              const articleViews = [AppView.NRI_GUIDE, AppView.RNOR_GUIDE, AppView.SCHOOL_GUIDE];
              const isActive = currentView === item.id ||
                (item.id === AppView.GUIDES && articleViews.includes(currentView as AppView));
              return (
                <button
                  key={item.id}
                  onClick={() => setView(item.id)}
                  className={`flex items-center space-x-3 w-full px-4 py-2.5 rounded-lg transition-colors duration-200
                    ${isActive
                      ? 'text-indigo-600 bg-indigo-50'
                      : 'text-slate-500 hover:text-slate-800 hover:bg-slate-50'
                    }`}
                >
                  <Icon size={18} className={isActive ? 'text-indigo-600' : 'text-slate-400'} />
                  <span className="text-sm font-medium">{item.label}</span>
                </button>
              );
            })}
          </div>
        ))}
      </div>

      {/* Mobile bottom bar — flat, scrollable */}
      <div className="flex md:hidden justify-around h-16 overflow-x-auto">
        {ALL_ITEMS.map((item) => {
          const Icon = item.icon;
          const isActive = currentView === item.id;
          return (
            <button
              key={item.id}
              onClick={() => setView(item.id)}
              className={`flex flex-col items-center justify-center p-2 min-w-[60px] transition-colors duration-200
                ${isActive ? 'text-indigo-600' : 'text-slate-500'}`}
            >
              <Icon size={20} />
              <span className="text-[10px] font-medium mt-1">{item.label}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
};

export default Navbar;
